// Dashboard de Ventas (Financiero → Resumen). Solo documentos emitidos que
// pasaron por "Procesar Emitidos" (financiero.tickets): facturas y notas de
// crédito, con las retenciones aplicadas a esas ventas.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FileMinus, FileText, Loader2, Receipt, ShieldCheck, TrendingUp, Users, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/lib/api/client';
import { GLASS, GLASS_BG } from '@/lib/glass';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const usd = (v: number | string | null | undefined) =>
  Number(v || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const num = (v: number | string | null | undefined) => Number(v || 0).toLocaleString(LOCALE);
const pct = (part: number | string | null | undefined, total: number | string | null | undefined) =>
  `${(Number(total) ? (Number(part || 0) / Number(total)) * 100 : 0).toFixed(1)}%`;
const MES_CORTO = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

interface Resumen {
  facturas_docs: number; facturas_monto: number; facturas_subtotal: number; facturas_iva: number;
  nc_docs: number; nc_monto: number; nc_aplicadas: number; nc_monto_aplicado: number;
  ret_docs: number; ret_monto: number; anticipos_monto: number; cobrado: number;
  venta_neta: number; por_cobrar: number; clientes: number;
  autorizados: number; pendientes: number; rechazados: number;
}
interface DashVentas {
  anio: number; resumen: Resumen;
  por_mes: Array<{ mes: number; facturas: number; nc: number; neto: number }>;
  por_dia?: Array<{ dia: number; facturas: number; nc: number; neto: number }>;
  top_clientes: Array<{ cliente: string | null; docs: number; neto: number }>;
  por_centro: Array<{ centro: string; centro_codigo: string | null; facturas: number; nc: number; docs: number; neto: number }>;
}

export function DashboardVentas() {
  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [mes, setMes] = useState<number | ''>('');
  const { data, isFetching } = useQuery<DashVentas>({
    queryKey: ['dashVentas', anio, mes],
    queryFn: async () => (await apiClient.post('/rpc/fin_dashboard_ventas', { _anio: anio, _mes: mes || null }, FIN)).data,
  });
  const r = data?.resumen;
  const chartData = mes
    ? (data?.por_dia ?? []).map((d) => ({ mes: String(d.dia), Facturas: Number(d.facturas), 'Notas de crédito': Number(d.nc), Neto: Number(d.neto) }))
    : (data?.por_mes ?? []).map((m) => ({ mes: MES_CORTO[m.mes - 1], Facturas: Number(m.facturas), 'Notas de crédito': Number(m.nc), Neto: Number(m.neto) }));
  const centros = data?.por_centro ?? [];
  const chartCentros = centros.map((c) => ({ centro: c.centro_codigo || c.centro, Neto: Number(c.neto) }));

  return (
    <div className={`p-6 space-y-5 min-h-full ${GLASS_BG}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Dashboard de Ventas</h1>
            <p className="text-sm text-muted-foreground">Documentos emitidos procesados (facturas y notas de crédito) · {anio}{mes ? ` · ${MES_CORTO[mes - 1]}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
            {Array.from({ length: anioActual - 2014 }, (_, i) => anioActual - i).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={mes} onChange={(e) => setMes(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Todo el año</option>
            {MES_CORTO.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>

      {isFetching && !data ? (
        <div className="py-16 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-primary" /></div>
      ) : !r ? (
        <p className="py-16 text-center text-muted-foreground">Sin datos.</p>
      ) : (
        <>
          {/* KPIs principales */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icono={FileText} color="text-blue-600" titulo="Facturas" valor={usd(r.facturas_monto)} sub={`${num(r.facturas_docs)} documentos`} />
            <Kpi icono={FileMinus} color="text-red-600" titulo="Notas de crédito" valor={usd(r.nc_monto)} sub={`${num(r.nc_docs)} docs · ${num(r.nc_aplicadas)} aplicadas (${usd(r.nc_monto_aplicado)})`} />
            <Kpi icono={Receipt} color="text-amber-600" titulo="Retenciones aplicadas" valor={usd(r.ret_monto)} sub={`${num(r.ret_docs)} aplicaciones`} />
            <Kpi icono={TrendingUp} color="text-green-700" titulo="Venta neta" valor={usd(r.venta_neta)} sub="Facturas − NC" />
          </div>

          {/* Estado de emisión al SRI: autorizados vs pendientes por autorizar */}
          <Card className={GLASS}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-3">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Documentos al SRI (facturas y NC del período)
              </div>
              <div className="flex flex-wrap items-center gap-x-10 gap-y-3">
                <div>
                  <p className="text-3xl font-bold text-emerald-600 tabular-nums">{num(r.autorizados)}</p>
                  <p className="text-xs text-muted-foreground">Autorizados</p>
                </div>
                <div className="text-2xl text-muted-foreground/50">vs</div>
                <div>
                  <p className={`text-3xl font-bold tabular-nums ${r.pendientes > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>{num(r.pendientes)}</p>
                  <p className="text-xs text-muted-foreground">Pendientes por autorizar</p>
                </div>
                {r.rechazados > 0 && (
                  <div className="border-l pl-6">
                    <p className="text-3xl font-bold text-red-600 tabular-nums">{num(r.rechazados)}</p>
                    <p className="text-xs text-muted-foreground">Rechazados / devueltos</p>
                  </div>
                )}
                <div className="ml-auto text-right">
                  <p className="text-xs text-muted-foreground">Autorizados</p>
                  <p className="text-lg font-semibold">{pct(r.autorizados, r.autorizados + r.pendientes + r.rechazados)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPIs secundarios */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Kpi small titulo="Subtotal" valor={usd(r.facturas_subtotal)} />
            <Kpi small titulo="IVA" valor={usd(r.facturas_iva)} />
            <Kpi small icono={Wallet} color="text-green-600" titulo="Cobrado" valor={usd(r.cobrado)} />
            <Kpi small titulo="Por cobrar" valor={usd(r.por_cobrar)} />
            <Kpi small icono={Users} titulo="Clientes" valor={num(r.clientes)} />
          </div>

          {/* Tendencia mensual */}
          <Card className={GLASS}>
            <CardHeader className="pb-2"><CardTitle className="text-base">{mes ? `Ventas por día · ${MES_CORTO[mes - 1]}` : 'Ventas por mes'}</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="mes" fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => usd(v)} />
                  <Legend />
                  <Bar dataKey="Facturas" fill="#2563eb" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Notas de crédito" fill="#dc2626" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Neto" fill="#16a34a" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Ventas por centro de costo */}
          <Card className={GLASS}>
            <CardHeader className="pb-2"><CardTitle className="text-base">Ventas por centro de costo</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {centros.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Sin ventas</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={Math.max(160, chartCentros.length * 34)}>
                    <BarChart data={chartCentros} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" fontSize={12} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="centro" fontSize={12} width={110} />
                      <Tooltip formatter={(v: number) => usd(v)} />
                      <Bar dataKey="Neto" fill="#0ea5e9" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-3">Centro de costo</th><th className="py-2 pr-3 text-right">Facturas</th>
                        <th className="py-2 pr-3 text-right">Docs</th><th className="py-2 pr-3 text-right">Notas de crédito</th>
                        <th className="py-2 pr-3 text-right">Venta neta</th><th className="py-2 text-right">% Part.</th>
                      </tr></thead>
                      <tbody>
                        {centros.map((c, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2 pr-3"><span title={c.centro}>{c.centro_codigo ? `${c.centro_codigo} · ` : ''}{c.centro}</span></td>
                            <td className="py-2 pr-3 text-right">{usd(c.facturas)}</td>
                            <td className="py-2 pr-3 text-right">{num(c.docs)}</td>
                            <td className="py-2 pr-3 text-right text-red-600">{usd(c.nc)}</td>
                            <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(c.neto)}</td>
                            <td className="py-2 text-right text-muted-foreground">{pct(c.neto, r.venta_neta)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Top clientes por venta neta */}
          <Card className={GLASS}>
            <CardHeader className="pb-2"><CardTitle className="text-base">Top 10 clientes por venta neta</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {(data?.top_clientes ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Sin ventas</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Cliente</th><th className="py-2 pr-3 text-right">Facturas</th><th className="py-2 pr-3 text-right">Venta neta</th><th className="py-2 text-right">% Part.</th>
                  </tr></thead>
                  <tbody>
                    {(data?.top_clientes ?? []).map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 pr-3"><p className="truncate max-w-md" title={c.cliente ?? ''}>{c.cliente || '—'}</p></td>
                        <td className="py-2 pr-3 text-right">{num(c.docs)}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(c.neto)}</td>
                        <td className="py-2 text-right text-muted-foreground">{pct(c.neto, r.venta_neta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ icono: Icono, color = 'text-muted-foreground', titulo, valor, sub, small }: {
  icono?: React.ComponentType<{ className?: string }>; color?: string; titulo: string; valor: string; sub?: string; small?: boolean;
}) {
  return (
    <Card className={GLASS}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          {Icono && <Icono className={`w-4 h-4 ${color}`} />}{titulo}
        </div>
        <p className={`font-bold ${small ? 'text-lg' : 'text-2xl'} mt-1`}>{valor}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}
